import React, { useState, useCallback, useMemo } from 'react';
import { FormFeedback } from 'reactstrap';
import PropTypes from 'prop-types';
import getDataUrlFromFile from 'utils/getDataUrlFromFile';
import { isValidImageMimeType } from 'utils/fileUploader';
import StyledFormLabel from 'components/form/StyledFormLabel';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';

import styled from 'styled-components';
import Tooltip, { getTooltipTarget } from 'components/Tooltip';

const PhotoPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  height: 72px;
  background: #fafafa;
  border: 1px solid #c8c8c8;
  border-radius: 5px;
  color: #767676;
  font-size: 24px;
  margin-bottom: 10px;
`;

const ImagePreviewImg = styled.img`
  object-fit: contain;
  max-width: 128px;
  height: 72px;
`;

const ImageInputWrapper = styled.div`
  display: ${props => (props.type === 'user' ? 'flex' : 'block')};
  justify-content: space-between;
  align-items: center;
  width: ${props => (props.type === 'user' ? '75%' : '100%')} input {
    font-size: 11px;
  }
`;

const Spacer = styled.div`
  margin-top: ${props => (props.type !== 'user' ? '30px' : '0')};
  width: 81%;
`;

const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
`;

const FileInputLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  height: 32px;
  background: #f0f2f6;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  margin-right: 16px;
`;

const StyledFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  clip-path: inset(50%);
  border: 0;
`;

const FileNameLabel = styled.label`
  font-size: 14px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: ${props => (props.fileTooLarge ? 'red' : '#33373d')};
`;

function PhotoInput({
  label,
  onChange,
  imageUri,
  imagePreviewNode,
  type,
  maxSizeMB = 2,
  tooltipProps
}) {
  const [imageTooLarge, setImageTooLarge] = useState(false);
  const [invalidImage, setInvalidImage] = useState(false);
  const [imagePreviewUri, setImagePreviewUri] = useState(null);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);

  const maxSizeBytes = 1048576 * maxSizeMB;
  const imageMsg = 'is invalid, please select a valid image';

  const imagePreviewUriToUse = useMemo(() => {
    return imagePreviewUri || imageUri;
  }, []);

  const onFileChange = useCallback(
    async e => {
      e.preventDefault();

      const file = e.target.files[0];

      if (!file) {
        setInvalidImage(true);
        setFile(null);
        setFileName('');
        setImagePreviewUri(null);
        return;
      }

      setFile(file);
      setFileName(file.name);

      const isValidMimeType = isValidImageMimeType(file.type);

      if (!isValidMimeType) {
        setInvalidImage(true);
      }

      const isValidSize = file && file.size <= maxSizeBytes;

      if (!isValidSize) {
        setImageTooLarge(true);
      }

      if (isValidMimeType && isValidSize) {
        onChange(file);
        const previewUri = await getDataUrlFromFile(file);
        setImagePreviewUri(previewUri);
        setImageTooLarge(false);
        setInvalidImage(false);
      } else {
        setFile(null);
        setFileName('');
        setImagePreviewUri(null);
      }
    },
    [onChange, maxSizeBytes]
  );

  return (
    <div>
      <StyledFormLabel htmlFor={`${type}Photo`}>
        {label ? label : 'Photo'}
      </StyledFormLabel>
      <ImageInputWrapper type={type} className="fs-exclude">
        {imagePreviewNode ? (
          imagePreviewNode
        ) : (
          <ImagePreview
            imageUri={imagePreviewUriToUse}
            type={type}
            tooltipProps={tooltipProps}
          />
        )}
        <Spacer type={type}>
          <FileInputWrapper>
            <FileInputLabel htmlFor={`${type}Photo`}>
              Choose file
              <StyledFileInput
                invalid={imageTooLarge || invalidImage}
                type="file"
                id={`${type}Photo`}
                onChange={onFileChange}
                accept="image/*"
                data-testid={`${type}Photo`}
              />
            </FileInputLabel>
            <FileNameLabel fileTooLarge={imageTooLarge}>
              {file?.name ||
                (imageTooLarge
                  ? 'Selected file is too large'
                  : 'No file chosen')}
            </FileNameLabel>
          </FileInputWrapper>
          {imageTooLarge && (
            <FormFeedback>
              Image size must not exceed {maxSizeMB}MB.
            </FormFeedback>
          )}

          {invalidImage && (
            <FormFeedback>{`${fileName} ${imageMsg}`}</FormFeedback>
          )}
        </Spacer>
      </ImageInputWrapper>
    </div>
  );
}

function ImagePreview({ imageUri, type, tooltipProps }) {
  const tooltipTarget = getTooltipTarget(tooltipProps);

  return (
    <>
      <Tooltip {...tooltipProps} />
      <div>
        {imageUri ? (
          <div style={{ marginBottom: '10px' }}>
            <ImagePreviewImg
              {...tooltipTarget}
              src={imageUri}
              alt={`${type} preview`}
            />
          </div>
        ) : (
          <PhotoPlaceholder {...tooltipTarget}>
            <FontAwesomeIcon alt={`${type} preview`} icon={faImage} />
          </PhotoPlaceholder>
        )}
      </div>
    </>
  );
}

PhotoInput.defaultProps = {
  maxSizeMB: 2
};

PhotoInput.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  imageUri: PropTypes.string,
  imagePreviewNode: PropTypes.string,
  type: PropTypes.string,
  maxSizeMB: PropTypes.number,
  tooltipProps: PropTypes.object
};

ImagePreview.propTypes = {
  imageUri: PropTypes.string,
  type: PropTypes.string,
  tooltipProps: PropTypes.object
};

export default PhotoInput;
